/**
 * TroubleshootingSection Component
 *
 * Expandable help section for device connection troubleshooting.
 * Shows common issues and solutions before directing users to support.
 */

import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Icon } from '../../../shared/Icon';

export interface TroubleshootingSectionProps {
  /** Whether the section is expanded */
  expanded?: boolean;
  /** Whether to highlight the section (e.g., after failed connection attempt) */
  highlighted?: boolean;
}

/**
 * TroubleshootingSection component
 */
export const TroubleshootingSection = React.forwardRef<
  HTMLDivElement,
  TroubleshootingSectionProps
>(
  (
    { expanded = false, highlighted = false }: TroubleshootingSectionProps,
    ref
  ): React.ReactElement => {
    const [isExpanded, setIsExpanded] = React.useState(expanded);

    // Sync with external expanded prop
    React.useEffect(() => {
      if (expanded) {
        setIsExpanded(true);
      }
    }, [expanded]);

    const toggleExpanded = (): void => {
      setIsExpanded(!isExpanded);
    };

    return (
      <Container ref={ref} $highlighted={highlighted}>
        <TriggerButton onClick={toggleExpanded} aria-expanded={isExpanded}>
          <TriggerContent>
            <Icon name='help' size='small' />
            <TriggerText>My device isn't appearing in the pop-up</TriggerText>
          </TriggerContent>
          <ExpandIcon $isExpanded={isExpanded}>
            <Icon name='expand_more' size='small' />
          </ExpandIcon>
        </TriggerButton>

        {isExpanded && (
          <ExpandedContent>
            <TroubleshootingList>
              <TroubleshootingItem>
                <ItemIcon>
                  <Icon name='info' size='small' color='var(--color-primary)' />
                </ItemIcon>
                <ItemContent>
                  <strong>Ensure you have Generation 5 plates</strong> (USB-C
                  charging port). If you have older Generation 3/4 plates,
                  please contact your sales representative or support for an
                  upgrade.
                </ItemContent>
              </TroubleshootingItem>

              <TroubleshootingItem>
                <ItemIcon>
                  <Icon name='usb' size='small' color='var(--color-primary)' />
                </ItemIcon>
                <ItemContent>
                  <strong>Check your USB connection.</strong> Make sure the
                  cable is securely connected to both your plates and computer.
                </ItemContent>
              </TroubleshootingItem>

              <TroubleshootingItem>
                <ItemIcon>
                  <Icon
                    name='restart_alt'
                    size='small'
                    color='var(--color-primary)'
                  />
                </ItemIcon>
                <ItemContent>
                  <strong>Try restarting the process.</strong> Hold the power
                  button until the plates turn off, then click{' '}
                  <strong>Start Over</strong> in the lower left corner. Pay
                  close attention to the recovery mode instructions.
                </ItemContent>
              </TroubleshootingItem>
            </TroubleshootingList>

            <SupportLink>
              Still having problems?{' '}
              <a
                href='https://www.hawkindynamics.com/support'
                target='_blank'
                rel='noopener noreferrer'
              >
                Contact Support
              </a>
            </SupportLink>
          </ExpandedContent>
        )}
      </Container>
    );
  }
);

TroubleshootingSection.displayName = 'TroubleshootingSection';

// Animations

const pulseHighlight = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb, 33, 150, 243), 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(var(--color-primary-rgb, 33, 150, 243), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb, 33, 150, 243), 0); }
`;

// Styled Components

const Container = styled.div<{ $highlighted: boolean }>`
  background: var(--color-surface-1);
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.3s ease;

  ${(props): ReturnType<typeof css> | string =>
    props.$highlighted
      ? css`
          border: 2px solid var(--color-primary);
          animation: ${pulseHighlight} 1s ease-out;
        `
      : ''}
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-text);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TriggerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TriggerText = styled.span`
  font-size: 13px;
  color: var(--color-text);
`;

const ExpandIcon = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: rotate(${(props): string => (props.$isExpanded ? '180deg' : '0')});
`;

const ExpandedContent = styled.div`
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const TroubleshootingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TroubleshootingItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const ItemIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const ItemContent = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);

  strong {
    color: var(--color-text);
  }
`;

const SupportLink = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
  text-align: center;

  a {
    color: var(--color-accent-light);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default TroubleshootingSection;
